import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@/utils/cn';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@/components/ui/Button';
import { useCart } from '@/Context/CartContext';
import useHeaderData from '@/hooks/useHeaderData';
import useFooterData from '@/hooks/useFooterData';
import { useScrollLock } from '@/hooks/useScrollLock';
import content, { brand, features, footer, header, contact, websiteSettings } from '@/data/content';

const AppHeader: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: headerData } = useHeaderData();
    const { data: footerData } = useFooterData();
    const socialLinks = footerData?.social_links ?? {};
    const { cartItems, toggleCart } = useCart();
    const [logoFailed, setLogoFailed] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    useScrollLock(menuOpen);

    const cartCount = useMemo(
        () => cartItems.reduce((sum: number, item) => sum + Number(item?.quantity || 0), 0),
        [cartItems]
    );

    const [cartBounce, setCartBounce] = useState(false);
    const prevCartCountRef = React.useRef(cartCount);

    useEffect(() => {
        if (cartCount > prevCartCountRef.current) {
            setCartBounce(true);
            const t = window.setTimeout(() => setCartBounce(false), 600);
            prevCartCountRef.current = cartCount;
            return () => window.clearTimeout(t);
        }
        prevCartCountRef.current = cartCount;
    }, [cartCount]);

    const candidateLogo = headerData?.logo_url;
    const logoSrc =
        typeof candidateLogo === 'string' &&
        candidateLogo.trim() !== '' &&
        !candidateLogo.includes('/assets/images/logo')
            ? candidateLogo
            : brand.logo;

    const rawPhone = headerData?.phone || '';
    const phoneHref = rawPhone ? `tel:${rawPhone.replace(/[^+\d]/g, '')}` : null;
    const isNavActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

    const handleMobileNavLinkClick = () => {
        setMenuOpen(false);
    };

    const navLinkClass = (to: string) =>
        cn(
            'relative text-sm font-semibold whitespace-nowrap transition-all duration-300 px-3 py-1.5 rounded-full hover:bg-app-primary/10 hover:text-app-primary',
            isNavActive(to) ? 'text-app-primary bg-app-primary/10' : 'text-app-text/80 dark:text-gray-300/80'
        );

    const navIconProps = {
        viewBox: '0 0 24 24',
        className: 'w-5 h-5 flex-shrink-0',
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: '1.8',
        strokeLinecap: 'round' as const,
        strokeLinejoin: 'round' as const,
        'aria-hidden': true,
    };

    const homeNavSvg = (
        <svg {...navIconProps}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <path d="M9 22V12h6v10" />
        </svg>
    );
    const menuNavSvg = (
        <svg {...navIconProps}>
            <path d="M3 2v6c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2M7 2v20" />
            <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h1v5a1 1 0 0 0 2 0V2" />
        </svg>
    );
    const aboutNavSvg = (
        <svg {...navIconProps}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
        </svg>
    );
    const servicesNavSvg = (
        <svg {...navIconProps}>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
    );
    const lockNavSvg = (
        <svg {...navIconProps}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    );

    const phoneSvg = (
        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.27 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.18 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const cartSvg = (
        <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path
                d="M3 4h2l2.2 10.1a2 2 0 0 0 2 1.6h8.7a2 2 0 0 0 2-1.6L23 7H7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle cx="10" cy="20" r="1.5" />
            <circle cx="18" cy="20" r="1.5" />
        </svg>
    );

    return (
        <>
            <header
                className="fixed top-0 inset-x-0 z-50 h-[58px] bg-white/80 dark:bg-gray-900/80 backdrop-blur-md transition-colors duration-300"
                role="banner"
            >
                <div className="h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    {/* ── Mobile: burger left slot ── */}
                    <div className="md:hidden flex items-center">
                        <Button
                            variant="ghost"
                            type="button"
                            className="p-2 -ml-2 !rounded-lg"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            aria-label={menuOpen ? header.close_menu_label : header.open_menu_label}
                        >
                            <span aria-hidden="true">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    {menuOpen ? (
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    ) : (
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                    )}
                                </svg>
                            </span>
                        </Button>
                    </div>

                    {/* ── Desktop: phone far-left ── */}
                    <div className="hidden md:flex flex-1 items-center">
                        {phoneHref && (
                            <a
                                href={phoneHref}
                                className="inline-flex items-center gap-2 text-sm font-bold text-app-primary border border-app-primary/30 bg-app-primary/5 px-3 py-1.5 rounded-full hover:bg-app-primary hover:text-white transition-all duration-200 shadow-sm"
                                aria-label="Appeler Bivin's Kitchen"
                            >
                                {phoneSvg}
                                <span>{rawPhone}</span>
                            </a>
                        )}
                    </div>

                    {/* ── Desktop: capsule — nav links + logo bulge (centered) ── */}
                    <div className="hidden md:flex items-center bg-white/80 dark:bg-gray-800/80 shadow-[0_8px_32px_rgba(232,84,26,0.15)] border border-white/60 dark:border-gray-700/50 backdrop-blur-xl rounded-full px-4 py-1 h-[46px] max-w-[calc(100%-200px)] absolute left-1/2 -translate-x-1/2 transition-all duration-500 hover:shadow-[0_8px_32px_rgba(232,84,26,0.25)] hover:border-app-primary/30">
                        <nav className="flex items-center gap-1.5" aria-label="Navigation principale">
                            <Link to="/menu" className={navLinkClass('/menu')}>
                                {header.menu_label}
                            </Link>
                            <Link to="/about-us" className={navLinkClass('/about-us')}>
                                {header.about_label}
                            </Link>
                        </nav>

                        <Link
                            to="/"
                            className="w-12 h-12 flex items-center justify-center bg-white dark:bg-gray-900 border border-app-primary/30 rounded-full mx-3 shadow-lg shadow-app-primary/20 hover:shadow-xl hover:shadow-app-primary/40 hover:border-app-primary hover:scale-105 transition-all duration-500 z-10 flex-shrink-0"
                            aria-label="Retour accueil"
                        >
                            {!logoFailed && logoSrc ? (
                                <img
                                    src={logoSrc}
                                    alt={brand.restaurant_name || "Bivin's Kitchen"}
                                    className="w-10 h-10 object-contain"
                                    onError={() => setLogoFailed(true)}
                                />
                            ) : (
                                <span className="font-medium text-xs">Bivin's</span>
                            )}
                        </Link>

                        <nav className="flex items-center gap-1.5" aria-label="Navigation secondaire">
                            <Link to="/services" className={navLinkClass('/services')}>
                                {header.services_label}
                            </Link>
                            <Link to="/contact" className={navLinkClass('/contact')}>
                                {header.contact_label}
                            </Link>
                        </nav>
                    </div>

                    {/* ── Mobile: logo centered ── */}
                    <div className="md:hidden flex-1 flex justify-center">
                        <Link to="/" className="flex items-center" aria-label="Retour accueil">
                            {!logoFailed && logoSrc ? (
                                <img
                                    src={logoSrc}
                                    alt="Bivin's Kitchen"
                                    className="h-14 w-auto max-w-[140px] object-contain"
                                    onError={() => setLogoFailed(true)}
                                />
                            ) : (
                                <span className="font-medium text-lg text-app-text">Bivin's Kitchen</span>
                            )}
                        </Link>
                    </div>

                    {/* ── Desktop: cart + profile far-right ── */}
                    <div className="hidden md:flex flex-1 items-center justify-end gap-3">
                        {features.order && (
                            <Button
                                variant="ghost"
                                type="button"
                                className="relative !w-10 !h-10 !p-0 !rounded-xl"
                                onClick={() => (cartCount > 0 ? toggleCart() : navigate('/menu'))}
                                aria-label="Panier"
                            >
                                <span
                                    className={cn('inline-flex', cartBounce && 'animate-bounce')}
                                    aria-hidden="true"
                                >
                                    {cartSvg}
                                </span>
                                {cartCount > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 rounded-full bg-app-primary text-white text-[10px] font-bold flex items-center justify-center px-1">
                                        {cartCount}
                                    </span>
                                )}
                            </Button>
                        )}
                        {features.login && (
                            <Button
                                type="button"
                                className="flex items-center justify-center w-10 h-10 rounded-xl bg-app-primary/5 border border-app-primary/20 text-app-primary hover:bg-app-primary/10 hover:-translate-y-0.5 hover:border-app-primary/40 transition-all duration-200 shadow-sm"
                                onClick={() => navigate('/login')}
                                aria-label="Connexion"
                            >
                                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                                        <path
                                            d="M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                            </Button>
                        )}
                    </div>

                    {/* ── Mobile: right actions — phone icon + cart ── */}
                    <div className="md:hidden flex items-center gap-2">
                        {phoneHref && (
                            <a
                                href={phoneHref}
                                className="flex items-center justify-center w-9 h-9 rounded-lg bg-app-primary/5 text-app-primary border border-app-primary/20"
                                aria-label="Appeler Bivin's Kitchen"
                            >
                                {phoneSvg}
                            </a>
                        )}
                        {features.order && (
                            <button
                                type="button"
                                className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-app-primary/5 text-app-primary border border-app-primary/20"
                                onClick={() => (cartCount > 0 ? toggleCart() : navigate('/menu'))}
                                aria-label="Panier"
                            >
                                <span
                                    className={cn('inline-flex', cartBounce && 'animate-bounce')}
                                    aria-hidden="true"
                                >
                                    {cartSvg}
                                </span>
                                {cartCount > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-app-primary text-white text-[9px] font-bold flex items-center justify-center px-1">
                                        {cartCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Mobile nav drawer */}
            {menuOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[60] backdrop-blur-sm transition-opacity"
                        onClick={() => setMenuOpen(false)}
                        aria-hidden="true"
                    />
                    <div
                        className="fixed top-0 left-0 bottom-0 w-4/5 max-w-sm bg-white dark:bg-gray-900 z-[70] flex flex-col shadow-2xl animate-in slide-in-from-left duration-300"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Menu de navigation"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
                            <span className="font-bold text-lg text-app-text dark:text-white">
                                {websiteSettings.restaurant_name}
                            </span>
                            {/* Mobile cart button */}
                            <button
                                type="button"
                                className="p-2 text-app-muted hover:text-app-text dark:hover:text-gray-300 rounded-lg bg-gray-50 dark:bg-gray-800"
                                onClick={() => setMenuOpen(false)}
                                aria-label={header.close_menu_label}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="flex-1 flex flex-col overflow-y-auto">
                            <nav className="flex flex-col py-4" aria-label="Navigation mobile">
                                <Link
                                    to="/"
                                    onClick={handleMobileNavLinkClick}
                                    className={cn(
                                        'flex items-center gap-4 px-6 py-4 text-base font-bold border-l-4 transition-colors',
                                        isNavActive('/') ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 text-app-primary' : 'border-transparent text-app-text/80 dark:text-gray-300 hover:bg-app-primary/5 dark:hover:bg-gray-800'
                                    )}
                                >
                                    {homeNavSvg}
                                    {header.home_label}
                                </Link>
                                <Link
                                    to="/menu"
                                    onClick={handleMobileNavLinkClick}
                                    className={cn(
                                        'flex items-center gap-4 px-6 py-4 text-base font-bold border-l-4 transition-colors',
                                        isNavActive('/menu') ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 text-app-primary' : 'border-transparent text-app-text/80 dark:text-gray-300 hover:bg-app-primary/5 dark:hover:bg-gray-800'
                                    )}
                                >
                                    {menuNavSvg}
                                    {header.menu_label}
                                </Link>
                                <Link
                                    to="/about-us"
                                    onClick={handleMobileNavLinkClick}
                                    className={cn(
                                        'flex items-center gap-4 px-6 py-4 text-base font-bold border-l-4 transition-colors',
                                        isNavActive('/about-us') ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 text-app-primary' : 'border-transparent text-app-text/80 dark:text-gray-300 hover:bg-app-primary/5 dark:hover:bg-gray-800'
                                    )}
                                >
                                    {aboutNavSvg}
                                    {header.about_label}
                                </Link>
                                <Link
                                    to="/services"
                                    onClick={handleMobileNavLinkClick}
                                    className={cn(
                                        'flex items-center gap-4 px-6 py-4 text-base font-bold border-l-4 transition-colors',
                                        isNavActive('/services') ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 text-app-primary' : 'border-transparent text-app-text/80 dark:text-gray-300 hover:bg-app-primary/5 dark:hover:bg-gray-800'
                                    )}
                                >
                                    {servicesNavSvg}
                                    {header.services_label}
                                </Link>
                                <Link
                                    to="/contact"
                                    onClick={handleMobileNavLinkClick}
                                    className={cn(
                                        'flex items-center gap-4 px-6 py-4 text-base font-bold border-l-4 transition-colors',
                                        isNavActive('/contact') ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 text-app-primary' : 'border-transparent text-app-text/80 dark:text-gray-300 hover:bg-app-primary/5 dark:hover:bg-gray-800'
                                    )}
                                >
                                    {phoneSvg}
                                    {header.contact_label}
                                </Link>

                                {features.login && (
                                    <Link
                                        to="/login"
                                        onClick={handleMobileNavLinkClick}
                                        className={cn(
                                            'flex items-center gap-4 px-6 py-4 text-base font-bold border-l-4 transition-colors',
                                            isNavActive('/login') ? 'border-app-primary bg-app-primary/5 dark:bg-app-primary/10 text-app-primary' : 'border-transparent text-app-text/80 dark:text-gray-300 hover:bg-app-primary/5 dark:hover:bg-gray-800'
                                        )}
                                    >
                                        {lockNavSvg}
                                        {header.login_label}
                                    </Link>
                                )}
                            </nav>
                        </div>

                        {/* Bottom Sticky Section */}
                        <div className="mt-auto border-t border-app-border dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                            {(socialLinks.instagram || socialLinks.facebook) && (
                                <div className="p-6 border-b border-app-border dark:border-gray-800">
                                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-app-muted mb-4 block">
                                        {contact.info.social_heading}
                                    </span>
                                    <div className="flex gap-4">
                                        {socialLinks.instagram && (
                                            <a
                                                href={socialLinks.instagram}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-12 h-12 flex items-center justify-center rounded-2xl text-white bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] shadow-lg shadow-pink-500/10 active:scale-95 transition-all"
                                                aria-label="Instagram"
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    className="w-6 h-6"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                                                    <circle cx="12" cy="12" r="4" />
                                                    <circle
                                                        cx="17.5"
                                                        cy="6.5"
                                                        r="0.5"
                                                        fill="currentColor"
                                                        stroke="none"
                                                    />
                                                </svg>
                                            </a>
                                        )}
                                        {socialLinks.facebook && (
                                            <a
                                                href={socialLinks.facebook}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="w-12 h-12 flex items-center justify-center rounded-2xl text-white bg-[#1877F2] shadow-lg shadow-blue-500/10 active:scale-95 transition-all"
                                                aria-label="Facebook"
                                            >
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    className="w-6 h-6"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-6 flex flex-col gap-3">
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-app-muted mb-1 block">
                                    {footer.legal_heading}
                                </span>
                                <Link
                                    to="/mentions-legales"
                                    className="text-sm font-bold text-app-text/70 dark:text-gray-400 hover:text-app-primary py-2.5 transition-colors"
                                >
                                    {footer.legal_info_label}
                                </Link>
                                <Link
                                    to="/cgv"
                                    className="text-sm font-bold text-app-text/70 dark:text-gray-400 hover:text-app-primary py-2.5 transition-colors"
                                >
                                    {footer.cgv_label}
                                </Link>
                                <Link
                                    to="/politique-confidentialite"
                                    className="text-sm font-bold text-app-text/70 dark:text-gray-400 hover:text-app-primary py-2.5 transition-colors"
                                >
                                    {footer.privacy_label}
                                </Link>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default AppHeader;
